import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { UnifiedAsset } from '@/hooks/useAssetsUnified';
import type { AssetsSortConfig } from '@/hooks/useAssetsUnifiedData';

interface UseAssetsTableLogicParams {
  assets: UnifiedAsset[];
  allAssets?: UnifiedAsset[];
  sortConfig: AssetsSortConfig;
  onSort: (field: AssetsSortConfig['field']) => void;
  onEdit: (asset: UnifiedAsset) => void;
  selectedAssets: string[];
  onSelectionChange: (assetIds: string[]) => void;
  onViewDetails?: (asset: UnifiedAsset) => void;
}

export const useAssetsTableLogic = ({
  assets,
  allAssets,
  sortConfig,
  onSort,
  onEdit,
  selectedAssets,
  onSelectionChange,
  onViewDetails: onViewDetailsProp
}: UseAssetsTableLogicParams) => {
  const navigate = useNavigate();

  // === SELECTION LOGIC ===
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      onSelectionChange((assets || []).map(asset => asset.id));
    } else {
      onSelectionChange([]);
    }
  }, [assets, onSelectionChange]);

  const handleSelectAsset = useCallback((assetId: string, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedAssets, assetId]);
    } else {
      onSelectionChange((selectedAssets || []).filter(id => id !== assetId));
    }
  }, [selectedAssets, onSelectionChange]);

  // === COMPUTED SELECTION STATE ===
  const isAllSelected = (assets?.length || 0) > 0 && (selectedAssets?.length || 0) === (assets?.length || 0);
  const isIndeterminate = (selectedAssets?.length || 0) > 0 && (selectedAssets?.length || 0) < (assets?.length || 0);

  // === SORTING LOGIC ===
  const getSortIcon = useCallback((field: AssetsSortConfig['field']) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3.5 w-3.5" />
      : <ArrowDown className="h-3.5 w-3.5" />;
  }, [sortConfig]);

  // === NAVIGATION LOGIC ===
  const handleRowClick = useCallback((asset: UnifiedAsset) => {
    if (onViewDetailsProp) {
      onViewDetailsProp(asset);
    } else {
      navigate(`/assets/${asset.id}`);
    }
  }, [onViewDetailsProp, navigate]);

  const handleViewDetails = useCallback((asset: UnifiedAsset) => {
    if (onViewDetailsProp) {
      onViewDetailsProp(asset);
    } else {
      navigate(`/assets/${asset.id}`);
    }
  }, [onViewDetailsProp, navigate]);

  // Open associated account for a holding row via account_identifier match
  const handleOpenAssociatedAccount = useCallback((asset: UnifiedAsset) => {
    if (asset.source_type !== 'connected') return;
    const list = (allAssets && allAssets.length ? allAssets : assets) || [];
    const account = list.find(a => a.source_type === 'account' && !!a.account_identifier && a.account_identifier === asset.account_identifier);
    if (account) {
      if (onViewDetailsProp) {
        onViewDetailsProp(account);
      } else {
        navigate(`/assets/${account.id}`);
      }
    }
  }, [assets, allAssets, onViewDetailsProp, navigate]);

  // === STYLING UTILITIES ===
  const getAssetTypeColor = useCallback((assetType: string) => {
    const colors = {
      cash: 'bg-green-100 text-green-800',
      equity: 'bg-blue-100 text-blue-800',
      fixed_income: 'bg-purple-100 text-purple-800',
      alternative: 'bg-orange-100 text-orange-800',
      holding: 'bg-gray-100 text-gray-800',
    };
    return colors[assetType as keyof typeof colors] || colors.holding;
  }, []);

  // === EVENT HANDLERS ===
  const handleEditAsset = useCallback((asset: UnifiedAsset) => {
    onEdit(asset);
  }, [onEdit]);

  const handleSort = useCallback((field: AssetsSortConfig['field']) => {
    onSort(field);
  }, [onSort]);

  // === RETURN INTERFACE ===
  return {
    // Selection state
    isAllSelected,
    isIndeterminate,
    
    // Event handlers
    handleSelectAll,
    handleSelectAsset,
    handleRowClick,
    handleViewDetails,
    handleEditAsset,
    handleSort,
    openAssociatedAccount: handleOpenAssociatedAccount,
    
    // Utility functions
    getSortIcon,
    getAssetTypeColor
  };
};
